import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, IconButton, TextField, createFilterOptions } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
const filterPresets = createFilterOptions({
    stringify: (option) => option.value,
    trim: true,
});
export const PresetAutocomplete = (props) => {
    const { name, presets, category, field, placeholder, disabled, onPresetSaved, onPresetDeleted } = props;
    const { control } = useFormContext();
    const queryClient = useQueryClient();
    // Local copy so a newly saved preset appears in the list immediately,
    // without waiting on a refetch from the care plan page.
    const [presetValues, setPresetValues] = useState([]);
    const [isSavingPreset, setIsSavingPreset] = useState(false);
    // Presets load async on the parent, so re-sync when they arrive/change.
    useEffect(() => {
        setPresetValues(dedupe(presets?.map((p) => p.value) ?? []));
    }, [presets]);
    const options = useMemo(() => presetValues.map((value) => ({ type: 'preset', value })), [presetValues]);
    const existsAlready = (value) => presetValues.some((v) => v.toLowerCase() === value.trim().toLowerCase());
    /**
     * Saves the currently typed text as a preset for this category/field.
     * Optimistically appends to the local list, and rolls back on failure.
     */
    const savePreset = async (rawValue) => {
        const value = rawValue.trim();
        if (!value || existsAlready(value) || isSavingPreset)
            return;
        setPresetValues((prev) => [...prev, value]);
        setIsSavingPreset(true);
        try {
            await axios.post('/api/carePlan/addPreset', { category, field, value });
            onPresetSaved?.(value);
            queryClient.invalidateQueries(['getCarePlanPresets']);
        }
        catch (error) {
            setPresetValues((prev) => prev.filter((v) => v !== value));
            console.error('Failed to save preset', error);
        }
        finally {
            setIsSavingPreset(false);
        }
    };
    /**
   * Removes a preset from this category/field.
   * Optimistically drops it from the local list, and restores it on failure.
   */
    const deletePreset = async (value) => {
        const id = presets?.find((p) => p.value === value)?.id;
        if (!id)
            return;
        const previous = presetValues;
        setPresetValues((prev) => prev.filter((v) => v !== value));
        try {
            // delete preset then reload by invalidating query 
            await axios.post('/api/carePlan/deletePreset', { id });
            queryClient.invalidateQueries(['getCarePlanPresets']);
            onPresetDeleted?.(value);
        }
        catch (error) {
            setPresetValues(previous);
            console.error('Failed to delete preset', error);
        }
    };
    return (React.createElement(Controller, { name: name, control: control, render: ({ field: rhfField, fieldState }) => (React.createElement(Autocomplete, { freeSolo: true, disableClearable: true, openOnFocus: true, selectOnFocus: true, handleHomeEndKeys: true, disabled: disabled, options: options, inputValue: rhfField.value ?? '', onInputChange: (_event, newInputValue) => {
                rhfField.onChange(newInputValue);
            }, onChange: (_event, newValue) => {
                if (typeof newValue === 'string')
                    return;
                if (newValue?.type === 'add') {
                    void savePreset(newValue.value);
                }
                // 'preset' rows need no handling — MUI writes the label back
                // through onInputChange, which already updates the form value.
            }, getOptionLabel: (option) => typeof option === 'string' ? option : option.value, filterOptions: (opts, params) => {
                const filtered = filterPresets(opts, params);
                const typed = params.inputValue.trim();
                if (typed !== '' && !existsAlready(typed)) {
                    filtered.push({ type: 'add', value: typed });
                }
                return filtered;
            }, groupBy: (option) => (option.type === 'preset' ? 'YOUR PRESETS' : ''), renderGroup: (params) => (React.createElement("li", { key: params.key },
                params.group && (React.createElement("div", { style: {
                        padding: '10px 16px 6px',
                        color: '#000000DE',
                        fontSize: 12,
                        fontFamily: 'Barlow',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                    } }, params.group)),
                React.createElement("ul", { style: { padding: 0, margin: 0, listStyle: 'none' } }, params.children))), renderOption: (optionProps, option) => {
                // MUI v6 puts `key` inside optionProps; v5 doesn't. Strip it either way
                // so we can set our own without React complaining about spread keys.
                const { key: _ignored, ...rest } = optionProps;
                if (option.type === 'add') {
                    return (React.createElement("li", { ...rest, key: "__add_preset__", style: {
                            borderTop: '1px solid #E5E7EB',
                            color: '#1976D2',
                            fontFamily: 'Barlow',
                            fontSize: 14,
                            padding: '12px 16px',
                        } }, isSavingPreset ? (React.createElement(React.Fragment, null,
                        React.createElement(CircularProgress, { size: 14, style: { marginRight: 8 } }),
                        "Saving\u2026")) : (React.createElement(React.Fragment, null,
                        "+ Save \u201C",
                        option.value,
                        "\u201D to presets"))));
                }
                return (React.createElement("li", { ...rest, key: `preset-${option.value}`, style: {
                        fontFamily: 'Barlow',
                        fontSize: 14,
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    } },
                    React.createElement("span", null, option.value),
                    React.createElement(IconButton, { className: "preset-delete", size: "small", onMouseDown: (e) => { e.preventDefault(); e.stopPropagation(); }, onClick: (e) => { e.preventDefault(); e.stopPropagation(); void deletePreset(option.value); }, sx: { padding: '2px', color: '#D32F2F' } },
                        React.createElement(DeleteOutlineIcon, { fontSize: "small" }))));
            }, slotProps: {
                paper: {
                    sx: {
                        borderRadius: '6px',
                        marginTop: '2px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        '& .MuiAutocomplete-listbox': {
                            padding: 0,
                            maxHeight: 260,
                        },
                        '& .MuiAutocomplete-option': {
                            minHeight: 'auto',
                        },
                        '& .MuiAutocomplete-option.Mui-focused': {
                            backgroundColor: '#DCEBF8 !important',
                        },
                        '& .MuiAutocomplete-option .preset-delete': { visibility: 'hidden' },
                        '& .MuiAutocomplete-option:hover .preset-delete': { visibility: 'visible' },
                        '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option:hover': {
                            backgroundColor: '#E3F2FD !important',
                        },
                    },
                },
            }, renderInput: (params) => (React.createElement(TextField, { ...params, inputRef: rhfField.ref, onBlur: rhfField.onBlur, variant: "outlined", size: "small", placeholder: placeholder, error: !!fieldState.error, helperText: fieldState.error?.message, style: { width: '100%' } })) })) }));
};
const dedupe = (values = []) => {
    const seen = new Set();
    return values.filter((v) => {
        const key = v.trim().toLowerCase();
        if (!key || seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
};
